import React, { forwardRef, useEffect, useState, useImperativeHandle, Fragment } from 'react';
import { injectIntl } from 'react-intl';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import ColoredLeftBorderCard from '../ColoredLeftBorderCard/ColoredLeftBorderCard';
import { decimalToString } from '../../../../utils/formatNumber';
import { useTheme } from '@mui/material/styles';
import { isFalseExceptZero } from '../../../../utils/helpers';
import ToggleEditButton from '../ToggleEditButton/ToggleEditButton';
import { NumberFormatWrapper } from '../../../../components/NumberFormatTextField/NumberFormatTextField';
import { usePrevious } from '../../../../utils/hooks';
import AssetDistributionChart from './AssetDistributionChart/AssetDistributionChart';
import ExpenseIncomeChart from './ExpenseIncomeChart/ExpenseIncomeChart';
import MonthlyExpensePurchaseMonthsChart from './MonthlyExpensePurchaseMonthsChart/MonthlyExpensePurchaseMonthsChart';
import { tracking } from '../../../../utils/tracking';
import ExpenseCalculatorTriggerButton from '../../../../components/ExpenseCalculatorDialog/ExpenseCalculatorTriggerButton/ExpenseCalculatorTriggerButton';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import _ from 'lodash';
import { focusRemainsInEditFlow, formatEditFieldLabel } from '../utils';

const EditButtonStyle = {
  position: 'absolute',
  top: 7,
  right: 6,
  zIndex: 2
};

// Representative colours for the monthly figures: amber for money going out,
// green for what's left over. Shared with ExpenseIncomeChart so the bar chart
// underneath uses the same colours as the figures above it.
const EXPENSE_COLOR = '#ffa600';
const SURPLUS_COLOR = '#3eda7d';

const ArrowIconStyle = {
  background: '#3966f8',
  color: '#fff',
  borderRadius: '50%',
  fontSize: 24
};

const InfoTypo = props => {
  const { label, value, valueEdit, valueStyle, ...rest } = props;

  return (
    <Grid
      container
      wrap={valueEdit ? 'nowrap' : undefined}
      spacing={1}
      {...rest}
      sx={[{
        justifyContent: "center",
        alignItems: "center"
      }, ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx])]}>
      <Grid>
        <Typography noWrap={!!valueEdit}>{label}</Typography>
      </Grid>
      <Grid>
        {!!valueEdit ? (
          valueEdit
        ) : (
          <Typography color="primary" style={{ fontWeight: 'bold', fontSize: '2.07rem', ...valueStyle }}>
            {value}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const InfoSection = forwardRef((props, ref) => {
  const {
    intl,
    clientId,
    factsheetId,
    currency,
    monthlyIncome,
    monthlyExpense,
    emergencyFund,
    pyramidData,
    onChange,
    template,
    expenseUpdateDetailsRef,
    showExpenseCalculator,
    onToggleShowExpenseCalculator,
    expenses
  } = props;
  const [internalMonthlyIncome, setInternalMonthlyIncome] = useState(monthlyIncome);
  const [internalMonthlyExpense, setInternalMonthlyExpense] = useState(monthlyExpense);
  const [internalEmergencyFund, setInternalEmergencyFund] = useState(emergencyFund);
  const [internalExpenses, setInternalExpenses] = useState(expenses);
  const prevMonthlyIncome = usePrevious(monthlyIncome);
  const prevMonthlyExpense = usePrevious(monthlyExpense);
  const prevEmergencyFund = usePrevious(emergencyFund);

  const theme = useTheme();
  const [isEdit, setIsEdit] = useState(false);
  const [isEditEmergencyFund, setIsEditEmergencyFund] = useState(false);
  const [monthlyFieldToFocus, setMonthlyFieldToFocus] = useState();

  const canEdit = !!onChange && !!clientId && !!factsheetId;
  const incomeEditLabel = formatEditFieldLabel(intl, intl.formatMessage({ id: 'Income' }));
  const expenseEditLabel = formatEditFieldLabel(intl, intl.formatMessage({ id: 'expense' }));
  const emergencyFundEditLabel = formatEditFieldLabel(intl, intl.formatMessage({ id: 'emg-fund' }));

  const applyMonthlyChanges = ({ forceApply = false, shouldTrack = false } = {}) => {
    const hasChanges =
      internalMonthlyIncome !== monthlyIncome ||
      internalMonthlyExpense !== monthlyExpense ||
      !_.isEqual(internalExpenses, expenses);

    if ((forceApply || hasChanges) && onChange) {
      if (shouldTrack) {
        tracking('- Click on Monthly expense setting - Financial pyramid');
      }
      onChange({
        monthlyIncome: internalMonthlyIncome,
        monthlyExpense: internalMonthlyExpense,
        expenses: internalExpenses
      });
    }
    setIsEdit(false);
  };

  const startMonthlyEditing = focusField => {
    if (!canEdit || isEdit) {
      return;
    }

    setInternalMonthlyIncome(monthlyIncome);
    setInternalMonthlyExpense(monthlyExpense);
    setInternalExpenses(expenses);
    setMonthlyFieldToFocus(focusField);
    setIsEdit(true);
  };

  const toggleIsEdit = () => {
    if (isEdit) {
      applyMonthlyChanges({ forceApply: true, shouldTrack: true });
    } else {
      startMonthlyEditing();
    }
  };

  const handleMonthlyBlur = event => {
    if (isEdit && !focusRemainsInEditFlow(event)) {
      applyMonthlyChanges();
    }
  };

  const applyEmergencyFundChanges = forceApply => {
    if ((forceApply || internalEmergencyFund !== emergencyFund) && onChange) {
      tracking('Click on Emergency asset - Financial pyramid');
      onChange({ emergencyFund: internalEmergencyFund });
    }
    setIsEditEmergencyFund(false);
  };

  const startEmergencyFundEditing = () => {
    if (!canEdit || isEditEmergencyFund) {
      return;
    }

    setInternalEmergencyFund(emergencyFund);
    setIsEditEmergencyFund(true);
  };

  const toggleIsEditEmergencyFund = () => {
    if (isEditEmergencyFund) {
      applyEmergencyFundChanges(true);
    } else {
      startEmergencyFundEditing();
    }
  };

  const handleEmergencyFundBlur = event => {
    if (isEditEmergencyFund && !focusRemainsInEditFlow(event)) {
      applyEmergencyFundChanges(false);
    }
  };

  const handleStartEditKeyDown = startEditing => event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      startEditing();
    }
  };

  const monthlyUsable =
    !isFalseExceptZero(internalMonthlyIncome) && !isFalseExceptZero(internalMonthlyExpense)
      ? internalMonthlyIncome - internalMonthlyExpense
      : undefined;
  const formatValueDisplay = value => (!isFalseExceptZero(value) ? decimalToString(value) : '-');

  const isOverspending =
    !isFalseExceptZero(monthlyExpense) && !isFalseExceptZero(monthlyIncome) && monthlyExpense > monthlyIncome;
  const incomeColor = theme.palette.primary.main;
  const expenseColor = isOverspending ? theme.palette.error.main : EXPENSE_COLOR;
  const surplusColor =
    monthlyUsable === undefined ? undefined : monthlyUsable < 0 ? theme.palette.error.main : SURPLUS_COLOR;

  const onChangeMonthlyIncome = event => setInternalMonthlyIncome(event.target.value);
  const onChangeMonthlyExpense = event => setInternalMonthlyExpense(event.target.value);
  const onChangeEmergencyFund = event => setInternalEmergencyFund(event.target.value);
  const onChangeMonthlyExpenseFromCalculator = (total, expenses) => {
    if (expenseUpdateDetailsRef) {
      expenseUpdateDetailsRef.current = { total: total };
    }
    onChange({ monthlyExpense: total, expenses: expenses });
    setInternalMonthlyExpense(total);
    setInternalExpenses(expenses);
  };

  useEffect(() => {
    if (monthlyIncome !== prevMonthlyIncome && monthlyIncome !== internalMonthlyIncome) {
      setInternalMonthlyIncome(monthlyIncome);
    }
  }, [monthlyIncome, internalMonthlyIncome, prevMonthlyIncome, clientId]);

  useEffect(() => {
    if (monthlyExpense !== prevMonthlyExpense && monthlyExpense !== internalMonthlyExpense) {
      setInternalMonthlyExpense(monthlyExpense);
    }
  }, [monthlyExpense, internalMonthlyExpense, prevMonthlyExpense, clientId]);

  useEffect(() => {
    if (emergencyFund !== prevEmergencyFund && emergencyFund !== internalEmergencyFund) {
      setInternalEmergencyFund(emergencyFund);
    }
  }, [emergencyFund, internalEmergencyFund, prevEmergencyFund, clientId]);

  useImperativeHandle(ref, () => ({
    toggleInfoEdit: flag => setIsEdit(flag !== undefined ? flag : !isEdit),
    toggleEmergencyFundEdit: flag => setIsEditEmergencyFund(flag !== undefined ? flag : !isEditEmergencyFund)
  }));

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid style={{ width: '100%' }} onBlur={handleMonthlyBlur}>
          <ColoredLeftBorderCard color="#3966f8" contentStyle={{ width: '100%', padding: 16 }}>
            {onChange && (
              <ToggleEditButton
                isEdit={isEdit}
                onChange={toggleIsEdit}
                style={EditButtonStyle}
                disabled={!clientId || !factsheetId}
              />
            )}

            <Grid container spacing={2} sx={{
              justifyContent: "center"
            }}>
              <Grid size={12}>
                <Typography align="left">{intl.formatMessage({ id: 'monthly' })}</Typography>
              </Grid>
              <Grid size={12}>
                <InfoTypo
                  label={`${intl.formatMessage({ id: 'Income' })}:`}
                  value={formatValueDisplay(internalMonthlyIncome)}
                  onClick={!isEdit && canEdit ? () => startMonthlyEditing('income') : undefined}
                  onKeyDown={!isEdit && canEdit ? handleStartEditKeyDown(() => startMonthlyEditing('income')) : undefined}
                  role={!isEdit && canEdit ? 'button' : undefined}
                  tabIndex={!isEdit && canEdit ? 0 : undefined}
                  aria-label={!isEdit && canEdit ? incomeEditLabel : undefined}
                  sx={!isEdit && canEdit ? { cursor: 'text' } : undefined}
                  valueEdit={
                    isEdit && (
                      <TextField
                        value={internalMonthlyIncome ?? ''}
                        autoFocus={monthlyFieldToFocus === 'income'}
                        onChange={onChangeMonthlyIncome}
                        slotProps={{
                          input: {
                            inputComponent: NumberFormatWrapper
                          }
                        }}
                      />
                    )
                  }
                />
              </Grid>
              <Grid size={12} style={{ position: 'relative' }}>
                {!template && !isEdit && (
                  <IconButton
                    color="primary"
                    onClick={onToggleShowExpenseCalculator}
                    style={{
                      position: 'absolute',
                      left: -4,
                      top: '50%',
                      transition: 'transform 0.8s',
                      transform: `translateY(-50%) rotate(${showExpenseCalculator ? 180 : 0}deg)`,
                      padding: 0,
                      boxShadow:
                        '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)'
                    }}
                    size="large">
                    <ArrowDropDownRoundedIcon style={ArrowIconStyle} />
                  </IconButton>
                )}

                <InfoTypo
                  label={`${intl.formatMessage({ id: 'expense' })}:`}
                  value={formatValueDisplay(internalMonthlyExpense)}
                  valueStyle={{ color: expenseColor }}
                  onClick={!isEdit && canEdit ? () => startMonthlyEditing('expense') : undefined}
                  onKeyDown={
                    !isEdit && canEdit ? handleStartEditKeyDown(() => startMonthlyEditing('expense')) : undefined
                  }
                  role={!isEdit && canEdit ? 'button' : undefined}
                  tabIndex={!isEdit && canEdit ? 0 : undefined}
                  aria-label={!isEdit && canEdit ? expenseEditLabel : undefined}
                  sx={!isEdit && canEdit ? { cursor: 'text' } : undefined}
                  valueEdit={
                    isEdit && (
                      <div style={{ position: 'relative' }}>
                        <TextField
                          value={internalMonthlyExpense ?? ''}
                          autoFocus={monthlyFieldToFocus === 'expense'}
                          onChange={onChangeMonthlyExpense}
                          slotProps={{
                            input: {
                              inputComponent: NumberFormatWrapper
                            }
                          }}
                        />
                        {clientId && factsheetId && (
                          <ExpenseCalculatorTriggerButton
                            expenses={expenses}
                            onSubmit={onChangeMonthlyExpenseFromCalculator}
                            style={{ position: 'absolute', right: 0, bottom: 6 }}
                          />
                        )}
                      </div>
                    )
                  }
                />
              </Grid>
              <Grid size={12}>
                <InfoTypo
                  label={`${intl.formatMessage({ id: 'surplus' })}:`}
                  value={formatValueDisplay(monthlyUsable)}
                  valueStyle={surplusColor ? { color: surplusColor } : undefined}
                />
              </Grid>
              <Grid size={12} style={{ height: 60 }}>
                <ExpenseIncomeChart
                  monthlyExpense={monthlyExpense}
                  monthlyIncome={monthlyIncome}
                  template={template}
                  expenseColor={expenseColor}
                  incomeColor={incomeColor}
                />
              </Grid>
              <Grid size={12} style={{ height: 166 }}>
                <AssetDistributionChart currency={currency} pyramidData={pyramidData} template={template} />
              </Grid>
            </Grid>
          </ColoredLeftBorderCard>
        </Grid>

        <Grid style={{ width: '100%' }} onBlur={handleEmergencyFundBlur}>
          <ColoredLeftBorderCard
            color="#3966f8"
            contentStyle={{ width: '100%', padding: '24px 16px 16px', whiteSpace: 'pre-wrap' }}
          >
            {onChange && (
              <ToggleEditButton
                isEdit={isEditEmergencyFund}
                onChange={toggleIsEditEmergencyFund}
                style={EditButtonStyle}
                disabled={!clientId || !factsheetId}
              />
            )}
            <Grid container spacing={2} sx={{
              justifyContent: "center"
            }}>
              <Grid size={12} style={{ zIndex: 1 }}>
                <InfoTypo
                  sx={{
                    justifyContent: "flex-start",
                    cursor: !isEditEmergencyFund && canEdit ? "text" : undefined
                  }}
                  label={`${intl.formatMessage({ id: 'emg-fund' })}:`}
                  value={formatValueDisplay(internalEmergencyFund)}
                  onClick={!isEditEmergencyFund && canEdit ? startEmergencyFundEditing : undefined}
                  onKeyDown={
                    !isEditEmergencyFund && canEdit
                      ? handleStartEditKeyDown(startEmergencyFundEditing)
                      : undefined
                  }
                  role={!isEditEmergencyFund && canEdit ? 'button' : undefined}
                  tabIndex={!isEditEmergencyFund && canEdit ? 0 : undefined}
                  aria-label={!isEditEmergencyFund && canEdit ? emergencyFundEditLabel : undefined}
                  valueEdit={
                    isEditEmergencyFund && (
                      <TextField
                        value={internalEmergencyFund ?? ''}
                        autoFocus={true}
                        onChange={onChangeEmergencyFund}
                        slotProps={{
                          input: {
                            inputComponent: NumberFormatWrapper
                          }
                        }}
                      />
                    )
                  }
                />
              </Grid>

              <Grid size={12}>
                <Typography color="textSecondary" align="center">
                  {intl.formatMessage({ id: 'fin-py-expense-months' })}
                </Typography>
              </Grid>
              <Grid size={12}>
                <MonthlyExpensePurchaseMonthsChart
                  value={monthlyExpense ? emergencyFund / monthlyExpense : 0}
                  template={template}
                />
              </Grid>
            </Grid>
          </ColoredLeftBorderCard>
        </Grid>
      </Grid>
    </Fragment>
  );
});

export default injectIntl(InfoSection, { withRef: true });
